import { dehydrate, QueryClient } from '@tanstack/react-query';

import EventsTicker from '@/components/Core/EventsTicker/EventsTicker';
import FullWidthVideo from '@/components/Core/FullWidthVIdeo/FullWidthVideo';
import HybridGamesIntro from '@/components/Core/HybridGamesIntro/HybridGamesIntro';
import SectionTitle from '@/components/Core/SectionTitle/SectionTitle';
import Timeline from '@/components/Core/Timeline/Timeline';
import MainLayout from '@/components/Layout/Main';
import { CustomMeta } from '@/components/Meta/CustomMeta';
import Button from '@/components/Primitive/Button/Button';
import useQuery, { makeQuery } from '@/hooks/use-query';
import { prefetchEvents } from '@/lib/prefetch-events';
import {
  createHowItWorksQuery,
  mapToHowItWorksContent,
} from '@/queries/globals/how-it-works';

const howItWorksQuery = createHowItWorksQuery();
const howItWorksVars = {};

const HowItWorks = () => {
  const { data } = useQuery(
    howItWorksQuery,
    howItWorksVars,
    mapToHowItWorksContent
  );
  const { featuredVideo, timeline } = data || {};
  return (
    <MainLayout withNavSpacer>
      <CustomMeta {...data?.seo} />
      <div className="pb-page space-y-page mx-auto w-full overflow-hidden">
        {Number(process.env.ENABLE_EVENTS_TICKER) === 1 && (
          <div className="hidden md:block">
            <EventsTicker />
          </div>
        )}
        <div className="container-md !mt-3 md:!mt-24">
          <HybridGamesIntro />
        </div>
        <div className="container-md flex flex-col items-center justify-center text-center">
          <SectionTitle intro="HOW It" title="WORKS" />
          <p className="max-w-3xl text-xl text-grayAlt-500 md:text-2xl">
            Hybrid games is a new kind of entertainment that combines the
            strengths of both the games industry and the TV industry. Our shows
            are broadcasted directly inside our games, giving YOU an immersive
            gaming experience unlike anything you&apos;ve seen before! Let us
            break it down for you...
          </p>
        </div>
        {featuredVideo?.videoId && (
          <div className="mx-auto">
            <FullWidthVideo
              video={{
                id: featuredVideo.videoId,
              }}
            />
          </div>
        )}
        {timeline && (
          <div className="container-base">
            <Timeline items={timeline} className="grow" />
          </div>
        )}
        <div className="container-md mx-auto flex max-w-2xl flex-col items-center justify-center space-y-10 text-center md:space-y-16">
          <div className="font-orton text-3xl uppercase text-cream md:text-4xl">
            Good luck, and <br /> Happy Gaming!
          </div>
          <Button className="uppercase" to="/play-now">
            Play now
          </Button>
        </div>
      </div>
    </MainLayout>
  );
};

export default HowItWorks;

export async function getStaticProps() {
  const queryClient = new QueryClient();
  await prefetchEvents(queryClient);

  await queryClient.prefetchQuery(
    ['query', howItWorksQuery, howItWorksVars],
    async () => {
      return makeQuery(howItWorksQuery, howItWorksVars, mapToHowItWorksContent);
    }
  );

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
    revalidate: 60,
  };
}
